"use client";

import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import SplitText from "gsap/SplitText";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps extends React.HTMLAttributes<HTMLElement> {
  items?: FAQItem[];
}

function FAQSection({
  items,
  className,
  ...props
}: FAQSectionProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const defaultFAQItems = t("faq.items", { returnObjects: true }) as FAQItem[];
  const faqItems = items ?? defaultFAQItems;

  useGSAP(() => {
    const ctx = gsap.context(() => {
      gsap.set(titleRef.current, { opacity: 0 })
      gsap.fromTo(titleRef.current, {opacity: 0}, {
        opacity: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: titleRef.current,
          start: "top 85%",
          end: "top 40%",
          scrub: 1,
        },
      })

      if(faqRef.current){
        const contentChildren = Array.from(faqRef.current.children);
        contentChildren.forEach((child) => {
          gsap.set(child, { y: 50, opacity: 0 });
        });
        gsap.fromTo(
          contentChildren,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              ease: "power4.out",
              stagger: {
                each: 0.1,
                from: "start",
              },
              scrollTrigger: {
                trigger: faqRef.current,
                start: "top center",
                end: "center center",
                scrub: 1,
              },
            }
          );
        }
    });

    return () => ctx.revert();
  });
  
  return (
    <section
      id="faq"
      className={cn(
        "px-6 py-12",
        "md:py-24",
        "2k:py-48",
        "4k:py-62", 
        className
      )}
      {...props}
    >
      <div className="flex flex-col md:flex-row">
        {/* Left side - Title */}
        <div className="flex-1">
          <h2 ref={titleRef} className="font-h1 sticky">
            {t("faq.title")}
          </h2>
        </div>

        {/* Right side - Accordion */}
        <div ref={faqRef} className="flex-1 pt-8 2k:pt-16 4k:pt-24 space-y-4 2k:space-y-12 4k:space-y-12">
          {faqItems.map((item, index) => (
            <div key={index}>
              <p className="text-[24px] 2k:text-[32px] 4k:text-[52px] font-bold">{item.question}</p>
              <p className="text-[24px] 2k:text-[32px] 4k:text-[52px]">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export { FAQSection, type FAQItem };
